import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '@/services/data/dataContext';
import { cn } from '@/lib/cn';
import { ActorMark } from '@/components/brand/ActorMark';

interface Props {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  showBack?: boolean;
}

export function TopBar({ title, subtitle, action, showBack = false }: Props) {
  const navigate = useNavigate();
  const { online, pendingWrites } = useAppData();

  return (
    <header className="bg-ink-50/95 dark:bg-ink-950/95 safe-top sticky top-0 z-30 backdrop-blur">
      <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
        {showBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="hover:bg-ink-100 dark:hover:bg-ink-800 -ml-2 flex size-11 items-center justify-center rounded-full"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="size-6" aria-hidden="true">
              <path d="m15 5-7 7 7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}

        <div className="min-w-0 flex-1">
          <h1 className="flex min-w-0 items-center gap-2 text-xl font-bold">
            {title === 'Line Runner' ? <ActorMark className="size-8 shrink-0" /> : null}
            <span className="truncate">{title}</span>
          </h1>
          {subtitle ? (
            <p className="text-ink-500 dark:text-ink-400 truncate text-sm">{subtitle}</p>
          ) : null}
        </div>

        {!online || pendingWrites > 0 ? (
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium',
              online ? 'bg-warn-400/20 text-warn-400' : 'bg-ink-200 dark:bg-ink-800 text-ink-500',
            )}
          >
            {online ? `Syncing ${pendingWrites}` : 'Offline'}
          </span>
        ) : null}

        {action}
      </div>
    </header>
  );
}
